import {QueryClient} from '@components/@query';
import {Cookie} from '@config/cookies';
import {getUrls} from '@config/endpoints';
import {QueryKey} from '@config/queryKeys';
import {Route} from '@config/routes';
import {http} from '@http/http';
import {checkJwtTokenValid, decodeJwt} from '@services/auth/auth.util';
import {dataLayerEmit} from '@services/gtm/datalayer.service';
import {UserService} from '@services/user/user.service';
import {ClientCookies} from '@utils/clientCookies';
import {getCookies} from '@utils/getCookies';
import {getIsServer} from '@utils/getIsServer';
import {getLocale} from '@utils/getLocale';
import {getPrependLocale} from '@utils/prependLocale';
import {redirect} from 'next/navigation';
import {IUser, UserRolesEnum} from '../user/user.model';
import {IJWTResponse, ILoginForm} from './auth.model';

export class AuthService {
    static authenticateSession = async (formData: ILoginForm) => {
        const {data} = await http.post<IJWTResponse>(getUrls().LOGIN, formData);
        await AuthService.addTokensToCookies(data.token, data.refreshToken);
    };

    static login = async (formData: ILoginForm) => {
        await this.authenticateSession(formData);

        // We need to refresh, so that the user query can refetch the user
        // and redirect us to authenticated part of the app.
        await UserService.refreshUser();

        const user = QueryClient.getQueryData<IUser>([QueryKey.USER]);

        if (user?.id) {
            await dataLayerEmit('Login', {
                login_method: 'email',
                user_id: user.id,
            });
        }
    };

    static refreshSession = async () => {
        const locale = await getLocale();
        const prependLocale = getPrependLocale(Route.LOGOUT, locale);
        if (getIsServer()) {
            // SSR session refresh is handled in middleware. If we get here, something went wrong
            redirect(prependLocale);
        } else {
            await this.refreshSessionCSR();
        }
    };

    static refreshSessionCSR = async () => {
        const refreshToken = await this.fetchRefreshToken();
        if (!refreshToken) {
            redirect(Route.LOGOUT);
        }
        const {data} = await http.post<IJWTResponse>(getUrls().LOGIN_REFRESH, {
            refreshToken: refreshToken,
        });
        if (!data || !data.token || !data.refreshToken) {
            redirect(Route.LOGOUT);
        }
        await this.addTokensToCookies(data.token, data.refreshToken);
    };

    static async getToken() {
        const cookies = await getCookies();
        return cookies.get(Cookie.AUTH_TOKEN)?.value || null;
    }

    static getTokenClient() {
        return ClientCookies.get(Cookie.AUTH_TOKEN)?.value || null;
    }

    static async fetchRefreshToken() {
        const cookies = await getCookies();
        return cookies.get(Cookie.REFRESH_TOKEN)?.value;
    }

    static async getHasToken() {
        const token = await AuthService.getToken();
        const refreshToken = await AuthService.fetchRefreshToken();
        return !!token || !!refreshToken;
    }

    static isTokenValid = async () => {
        const token = await AuthService.getToken();
        if (!token) {
            return false;
        }
        return checkJwtTokenValid(token);
    };

    static addTokensToCookies = async (token: string, refreshToken: string) => {
        const jwtData = await decodeJwt(token);

        const authTokenCookie = `${token}; Path=/; Expires=${jwtData.payload.exp.toUTCString()}`;
        const refreshTokenCookie = `${refreshToken}; Path=/; Max-Age=31536000`;

        const cookies = await getCookies();
        cookies.set(Cookie.AUTH_TOKEN, authTokenCookie);
        cookies.set(Cookie.REFRESH_TOKEN, refreshTokenCookie);
    };

    static removeTokensFromCookies = async () => {
        const cookies = await getCookies();
        cookies.delete(Cookie.AUTH_TOKEN);
        cookies.delete(Cookie.REFRESH_TOKEN);
    };

    static getIsAdmin = async () => {
        const token = await AuthService.getToken();

        if (!token) {
            return false;
        }

        try {
            const {payload} = await decodeJwt(token);
            return (
                payload &&
                (payload.roles.includes(UserRolesEnum.ADMIN) ||
                    payload.roles.includes(UserRolesEnum.SUPER_ADMIN))
            );
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    static refreshForNewHeaders = async (
        refreshToken: string,
        headers: Headers,
    ) => {
        try {
            const {data} = await http.post<IJWTResponse>(
                getUrls().LOGIN_REFRESH,
                {
                    refreshToken: refreshToken,
                },
            );
            const tokenData = await decodeJwt(data.token);
            headers.append(
                'Set-Cookie',
                `${Cookie.AUTH_TOKEN}=${data.token}; Path=/; Expires${tokenData.payload.exp}`,
            );
            headers.append(
                'Set-Cookie',
                `${Cookie.REFRESH_TOKEN}=${data.refreshToken}; Path=/; Max-Age=9000000`,
            );
        } catch (error) {
            headers.append(
                'Set-Cookie',
                `${Cookie.AUTH_TOKEN}=; Path=/; Max-Age=0`,
            );
            headers.append(
                'Set-Cookie',
                `${Cookie.REFRESH_TOKEN}=; Path=/; Max-Age=0`,
            );
            throw error;
        }
    };
}
